import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PatientTags = ({ uhid, tags = [], canEdit = false, onTagsUpdated, className = "", tone = "default" }) => {
  const [tagInput, setTagInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const onTint = tone === "onTint";

  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  const saveTags = async (nextTags) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/patients/${uhid}`, { tags: nextTags });
      onTagsUpdated?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update tags");
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = async () => {
    const trimmed = tagInput.trim();
    setAdding(false);
    setTagInput("");
    if (!trimmed) return;
    if (tags.includes(trimmed)) {
      toast.error("Tag already exists");
      return;
    }
    await saveTags([...tags, trimmed]);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
    if (e.key === "Escape") {
      setAdding(false);
      setTagInput("");
    }
  };

  const handleRemoveTag = async (tag) => {
    await saveTags(tags.filter((t) => t !== tag));
  };

  if (!canEdit && (tags?.length ?? 0) === 0) return null;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="patient-tags">
      {tags.map((tag) => (
        <Badge
          key={tag}
          variant="secondary"
          className={
            onTint
              ? "text-xs font-normal gap-1 pr-1 border-transparent bg-[var(--xl-primary)] text-white hover:bg-[var(--xl-primary-hover)]"
              : "text-xs font-normal gap-1 pr-1"
          }
        >
          {tag}
          {canEdit && (
            <button
              type="button"
              onClick={() => handleRemoveTag(tag)}
              disabled={saving}
              className={
                onTint
                  ? "rounded-full p-0.5 hover:bg-white/20 disabled:opacity-50"
                  : "rounded-full p-0.5 hover:bg-background/60 disabled:opacity-50"
              }
              aria-label={`Remove ${tag}`}
              data-testid={`remove-tag-${tag}`}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </Badge>
      ))}

      {canEdit && adding && (
        <Input
          ref={inputRef}
          data-testid="patient-tag-input"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onBlur={handleAddTag}
          placeholder="Tag name"
          disabled={saving}
          className="h-6 w-28 text-xs px-2 py-0"
        />
      )}

      {canEdit && !adding && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          disabled={saving}
          data-testid="patient-add-tag"
          className={
            onTint
              ? "inline-flex items-center gap-1 rounded-full border-transparent bg-[var(--xl-primary)] px-2 py-0.5 text-xs text-white hover:bg-[var(--xl-primary-hover)] transition-colors disabled:opacity-50"
              : "inline-flex items-center gap-1 rounded-full border border-dashed border-muted-foreground/40 px-2 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
          }
        >
          <Plus className="w-3 h-3" />
          Tag
        </button>
      )}
    </div>
  );
};

export default PatientTags;
